import React, { useRef, useEffect, useState } from "react";
import { Play, Pause, SkipForward, SkipBack, ExternalLink, Clock } from "lucide-react";
import { formatTime } from "../utils/videoSyncManager";

function MetadataSyncPlayer({ videoUrl, platform, isHost, socketManager, partyStatus, episodeInfo }) {
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [lastAction, setLastAction] = useState(null);
  const [showControls, setShowControls] = useState(isHost);
  const timeInputRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const lastTimeUpdateRef = useRef(Date.now());

  // Auto-increment timer when playing (for host)
  useEffect(() => {
    if (!isHost) return;

    if (isPlaying) {
      // Start the timer
      lastTimeUpdateRef.current = Date.now();
      timerIntervalRef.current = setInterval(() => {
        setCurrentTime(prev => {
          const now = Date.now();
          const elapsed = (now - lastTimeUpdateRef.current) / 1000;
          lastTimeUpdateRef.current = now;
          return prev + elapsed;
        });
      }, 100); // Update every 100ms for smooth counting
    } else {
      // Stop the timer
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [isPlaying, isHost]);

  // Platform display names
  const platformNames = {
    CRUNCHYROLL: "Crunchyroll",
    NETFLIX: "Netflix",
    HULU: "Hulu",
    DISNEY_PLUS: "Disney+",
    FUNIMATION: "Funimation",
  };

  // Platform colors
  const platformColors = {
    CRUNCHYROLL: "from-orange-500 to-orange-600",
    NETFLIX: "from-red-600 to-red-700",
    HULU: "from-green-500 to-green-600",
    DISNEY_PLUS: "from-blue-600 to-blue-700",
    FUNIMATION: "from-purple-600 to-purple-700",
  };

  // Listen to sync events
  useEffect(() => {
    if (!socketManager) return;

    const handleSyncState = (data) => {
      const { currentTime: targetTime, isPlaying: targetPlaying } = data;
      setCurrentTime(targetTime);
      setIsPlaying(targetPlaying);
      
      if (!isHost) {
        setLastAction({
          type: targetPlaying ? "play" : "pause",
          time: targetTime,
          timestamp: Date.now(),
        });
      }
    };

    const handlePlay = (data) => {
      const { currentTime: targetTime } = data;
      setCurrentTime(targetTime);
      setIsPlaying(true);
      
      if (!isHost) {
        setLastAction({
          type: "play",
          time: targetTime,
          timestamp: Date.now(),
        });
      }
    };

    const handlePause = (data) => {
      const { currentTime: targetTime } = data;
      setCurrentTime(targetTime);
      setIsPlaying(false);
      
      if (!isHost) {
        setLastAction({
          type: "pause",
          time: targetTime,
          timestamp: Date.now(),
        });
      }
    };

    const handleSeek = (data) => {
      const { currentTime: targetTime } = data;
      setCurrentTime(targetTime);
      
      if (!isHost) {
        setLastAction({
          type: "seek",
          time: targetTime,
          timestamp: Date.now(),
        });
      }
    };

    socketManager.on("sync-state", handleSyncState);
    socketManager.on("play", handlePlay);
    socketManager.on("pause", handlePause);
    socketManager.on("seek", handleSeek);

    // Request initial sync
    if (!isHost) {
      setTimeout(() => socketManager.requestSync(), 500);
    }

    return () => {
      socketManager.off("sync-state", handleSyncState);
      socketManager.off("play", handlePlay);
      socketManager.off("pause", handlePause);
      socketManager.off("seek", handleSeek);
    };
  }, [socketManager, isHost]);

  // Host controls
  const handleHostPlay = () => {
    const playTime = Math.floor(currentTime);
    socketManager.emitPlay(playTime);
    setIsPlaying(true);
    lastTimeUpdateRef.current = Date.now();
    setLastAction({
      type: "play",
      time: playTime,
      timestamp: Date.now(),
    });
    
    // Send auto chat notification
    socketManager.sendMessage(`▶️ Host resumed playback at ${formatTime(playTime)}`);
  };

  const handleHostPause = () => {
    const pauseTime = Math.floor(currentTime);
    socketManager.emitPause(pauseTime);
    setIsPlaying(false);
    setLastAction({
      type: "pause",
      time: pauseTime,
      timestamp: Date.now(),
    });
    
    // Send auto chat notification
    socketManager.sendMessage(`⏸️ Host paused at ${formatTime(pauseTime)}`);
  };

  const handleHostSeek = (seconds) => {
    const newTime = Math.max(0, currentTime + seconds);
    setCurrentTime(newTime);
    socketManager.emitSeek(newTime);
    setLastAction({
      type: "seek",
      time: newTime,
      timestamp: Date.now(),
    });
    
    // Send auto chat notification
    const direction = seconds > 0 ? 'forward' : 'backward';
    socketManager.sendMessage(`⏩ Host skipped ${direction} ${Math.abs(seconds)}s to ${formatTime(newTime)}`);
  };

  const handleTimeInput = () => {
    const input = timeInputRef.current.value;
    const parts = input.split(":").map(p => parseInt(p) || 0);
    let seconds = 0;
    
    if (parts.length === 3) {
      // HH:MM:SS
      seconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
    } else if (parts.length === 2) {
      // MM:SS
      seconds = parts[0] * 60 + parts[1];
    } else if (parts.length === 1) {
      // SS
      seconds = parts[0];
    }
    
    setCurrentTime(seconds);
    socketManager.emitSeek(seconds);
    setLastAction({
      type: "seek",
      time: seconds,
      timestamp: Date.now(),
    });
    
    // Send auto chat notification
    socketManager.sendMessage(`⏩ Host jumped to ${formatTime(seconds)}`);
    
    // Clear input
    timeInputRef.current.value = '';
  };

  const getActionText = () => {
    if (!lastAction) return null;
    
    const timeAgo = Math.floor((Date.now() - lastAction.timestamp) / 1000);
    const timeText = timeAgo < 5 ? "just now" : `${timeAgo}s ago`;
    
    switch (lastAction.type) {
      case "play":
        return `▶️ Host resumed at ${formatTime(lastAction.time)} (${timeText})`;
      case "pause":
        return `⏸️ Host paused at ${formatTime(lastAction.time)} (${timeText})`;
      case "seek":
        return `⏩ Host jumped to ${formatTime(lastAction.time)} (${timeText})`;
      default:
        return null;
    }
  };

  return (
    <div className="relative bg-gradient-to-br from-slate-950 to-slate-900 rounded-xl overflow-hidden border border-slate-700">
      {/* Header with Platform Badge */}
      <div className={`bg-gradient-to-r ${platformColors[platform] || "from-slate-700 to-slate-800"} px-6 py-4`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-white font-semibold text-lg">
              {platformNames[platform] || platform} Watch Party
            </h3>
            <p className="text-white/80 text-sm">Metadata Sync Mode</p>
          </div>
          {isHost && (
            <div className="bg-white/20 text-white px-3 py-1.5 rounded-full text-xs font-semibold">
              HOST
            </div>
          )}
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Episode Information Banner */}
        {(episodeInfo?.season_number || episodeInfo?.episode_number || episodeInfo?.episode_title) && (
          <div className="bg-gradient-to-r from-emerald-500/20 to-emerald-600/20 border-2 border-emerald-500/50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="bg-emerald-500 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg flex-shrink-0">
                
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-emerald-100 font-semibold text-sm mb-0.5">Now Watching:</div>
                <div className="text-white font-bold text-lg">
                  {episodeInfo.season_number && episodeInfo.episode_number ? (
                    <>Season {episodeInfo.season_number}, Episode {episodeInfo.episode_number}</>
                  ) : episodeInfo.season_number ? (
                    <>Season {episodeInfo.season_number}</>
                  ) : episodeInfo.episode_number ? (
                    <>Episode {episodeInfo.episode_number}</>
                  ) : null}
                  {episodeInfo.episode_title && (
                    <div className="text-emerald-200 text-sm font-normal mt-1 truncate">
                      "{episodeInfo.episode_title}"
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Instructions */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <ExternalLink className="text-amber-400 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <h4 className="text-amber-200 font-semibold mb-2"> How Manual Sync Works:</h4>
              <ol className="text-amber-200/80 text-sm space-y-2 list-decimal list-inside">
                <li>
                  <strong>Open {platformNames[platform]}:</strong> Click the button below to open in a new tab
                </li>
                <li>
                  <strong>{isHost ? "Control YOUR video:" : "Watch for commands:"}</strong>
                  {isHost 
                    ? " Play/pause your Netflix tab, then click the buttons below to notify participants"
                    : " When you see notifications above, manually sync your Netflix tab to match"}
                </li>
                <li>
                  <strong>Keep this tab open:</strong> You need both tabs - Netflix for video, this for chat & sync
                </li>
              </ol>
              <div className="mt-3 pt-3 border-t border-amber-500/30">
                <p className="text-amber-300 text-xs">
                   <strong>Note:</strong> The buttons {isHost ? "below" : "above"} don't automatically control {platformNames[platform]}. 
                  {isHost ? " They broadcast your actions to participants." : " You must manually follow the host's commands."}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Open Link Button */}
        <a
          href={videoUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white font-semibold py-4 rounded-lg transition flex items-center justify-center gap-2 text-center"
        >
          <ExternalLink size={20} />
          Open in {platformNames[platform]}
        </a>

        {/* Current State Display */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Clock size={18} className="text-slate-400" />
              <span className="text-slate-300 text-sm font-medium">Current Position</span>
            </div>
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold ${
              isPlaying 
                ? "bg-emerald-500/20 text-emerald-400" 
                : "bg-slate-700 text-slate-400"
            }`}>
              {isPlaying ? <Play size={14} /> : <Pause size={14} />}
              {isPlaying ? "Playing" : "Paused"}
            </div>
          </div>
          
          <div className="text-3xl font-bold text-slate-50 text-center">
            {formatTime(currentTime)}
          </div>

          {/* Last Action Notification */}
          {!isHost && lastAction && (
            <div className="mt-3 pt-3 border-t border-slate-700">
              <p className="text-sm text-slate-400 text-center">
                {getActionText()}
              </p>
            </div>
          )}
        </div>

        {/* Host Controls */}
        {isHost && (
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 space-y-4">
            <div>
              <h4 className="text-slate-200 font-semibold flex items-center gap-2 mb-1">
                 Host Controls
              </h4>
              <p className="text-xs text-slate-400">
                First, control playback in your {platformNames[platform]} tab. Then click these buttons to notify participants.
              </p>
            </div>

            {/* Play/Pause */}
            <div className="flex gap-2">
              <button
                onClick={handleHostPlay}
                disabled={isPlaying}
                className="flex-1 bg-emerald-500 hover:bg-emerald-400 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium py-3 rounded-lg transition flex items-center justify-center gap-2"
              >
                <Play size={18} />
                Play
              </button>
              <button
                onClick={handleHostPause}
                disabled={!isPlaying}
                className="flex-1 bg-slate-600 hover:bg-slate-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium py-3 rounded-lg transition flex items-center justify-center gap-2"
              >
                <Pause size={18} />
                Pause
              </button>
            </div>

            {/* Skip Controls */}
            <div className="grid grid-cols-4 gap-2">
              <button
                onClick={() => handleHostSeek(-30)}
                className="bg-slate-700 hover:bg-slate-600 text-white py-2 rounded-lg transition flex flex-col items-center justify-center text-xs"
              >
                <SkipBack size={16} />
                -30s
              </button>
              <button
                onClick={() => handleHostSeek(-10)}
                className="bg-slate-700 hover:bg-slate-600 text-white py-2 rounded-lg transition flex flex-col items-center justify-center text-xs"
              >
                <SkipBack size={16} />
                -10s
              </button>
              <button
                onClick={() => handleHostSeek(10)}
                className="bg-slate-700 hover:bg-slate-600 text-white py-2 rounded-lg transition flex flex-col items-center justify-center text-xs"
              >
                <SkipForward size={16} />
                +10s
              </button>
              <button
                onClick={() => handleHostSeek(30)}
                className="bg-slate-700 hover:bg-slate-600 text-white py-2 rounded-lg transition flex flex-col items-center justify-center text-xs"
              >
                <SkipForward size={16} />
                +30s
              </button>
            </div>

            {/* Manual Time Input */}
            <div>
              <label className="block text-sm text-slate-400 mb-2">
                Jump to specific time (MM:SS or HH:MM:SS):
              </label>
              <div className="flex gap-2">
                <input
                  ref={timeInputRef}
                  type="text"
                  placeholder="5:30"
                  className="flex-1 bg-slate-900 border border-slate-700 text-slate-100 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                />
                <button
                  onClick={handleTimeInput}
                  className="bg-emerald-500 hover:bg-emerald-400 text-white px-4 py-2 rounded-lg transition text-sm font-medium"
                >
                  Jump
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Participant Instructions */}
        {!isHost && (
          <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-4">
            <h4 className="text-violet-200 font-semibold mb-2">Following Host</h4>
            <p className="text-violet-200/80 text-sm mb-3">
              When you see a command above (like "▶️ Host resumed at 5:23"), <strong>manually</strong> play/pause/seek 
              your {platformNames[platform]} tab to match that exact time.
            </p>
            <div className="bg-violet-500/10 rounded p-2 text-xs text-violet-300">
              <strong>Example:</strong> If you see "Host paused at 2:15" → Go to your Netflix tab and pause at 2:15
            </div>
          </div>
        )}

        {/* Status Message */}
        {partyStatus === "waiting" && (
          <div className="text-center text-sm text-slate-400">
            Waiting for party to start...
          </div>
        )}
      </div>
    </div>
  );
}

export default MetadataSyncPlayer;
